// Phase 5 C5 — map filters + counter chips.
// Phase 6.9 C1 — the panel is now collapsible.
//
// Compact panel above the map. Two filters:
//
//   1. Toggle «только с задачами» — hides sleeping stations
//      (`total_jobs == 0`). Default OFF (show everything).
//   2. GNSS-config multi-select dropdown — reuses Phase 6.6's
//      `useAvailableConfigs` hook so the list reflects every
//      combo actually present in the DB. Default empty (show all).
//
// Phase 6.9 C1 — on a 380-px Telegram WebView the always-open
// two-row panel clipped its own labels. The panel now collapses to
// a single «Фильтры ▾ · N с данными» strip; clicking the strip (or
// clicking outside) toggles it. The collapsed/expanded choice is
// persisted in localStorage (`gnss-qa:map-filters-collapsed`) and
// defaults to collapsed. Visual pattern mirrors the Аналитика
// «GNSS-системы ▾» accordion.
//
// Spec §E.4 — the filter VALUES (toggle / configs) are still NOT
// persisted; only the panel's collapsed state is. Each map open
// starts with the default filters.
//
// Spec §E.3 — filters drive marker opacity, not unmount. The
// parent (MapScreen) computes `isVisible(station, filters)` and
// passes a 0/1 opacity multiplier to each marker.

import { useEffect, useMemo, useRef, useState } from 'react';

import { useAvailableConfigs } from '../../hooks/useAvailableConfigs';
import type { MapStation } from '../../api/types';

import './MapFilters.css';

export interface MapFilterState {
  onlyWithJobs: boolean;
  /** Selected canonical config keys (empty = «все»). A station is
   *  visible if any of its jobs match at least one selected key,
   *  but the map endpoint doesn't ship per-station config lists;
   *  see `stationMatchesConfigs` for the approximation we use. */
  configs: string[];
}

export const EMPTY_FILTERS: MapFilterState = {
  onlyWithJobs: false,
  configs: [],
};

/** Phase 6.9 C1 — localStorage key for the panel collapsed state.
 *  Follows the `gnss-qa:<name>` project convention (siblings:
 *  `gnss-qa:map-viewport`, `gnss-qa:analytics-filters`). Privacy-mode
 *  Telegram WebView throws on localStorage access — every touch is
 *  try/catch-guarded. */
const COLLAPSED_STORAGE_KEY = 'gnss-qa:map-filters-collapsed';

/** Read the persisted collapsed state. Defaults to `true`
 *  (collapsed) — both when nothing is stored and when storage is
 *  unreadable. */
function _readCollapsed(): boolean {
  try {
    const raw = localStorage.getItem(COLLAPSED_STORAGE_KEY);
    if (raw === null) return true;
    return raw === 'true';
  } catch {
    return true;
  }
}

function _writeCollapsed(v: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_STORAGE_KEY, String(v));
  } catch {
    /* ignore storage failures in privacy-mode WebView */
  }
}

/** Visibility decision per station.
 *
 *  The «only-with-jobs» half is unambiguous (`total_jobs > 0`).
 *
 *  Phase G.1 — the config-multi-select filter NO LONGER hides
 *  stations. Pre-G.1 it used `best_config` as an approximation and
 *  hid stations whose «лучший» wasn't in the selection — but this
 *  silently dropped stations that had jobs in the selected configs
 *  (just not as their best). The honest answer is to keep every
 *  station on the map and recolor them per the `per_config` slice
 *  in `StationMarker.markerColorForSelection`: gray = «не пробовали»,
 *  black = «пробовали, не получилось», colour = «есть результат». */
export function stationMatchesFilters(
  station: MapStation,
  filters: MapFilterState,
): boolean {
  if (filters.onlyWithJobs && station.total_jobs === 0) return false;
  return true;
}

interface Props {
  filters: MapFilterState;
  onChange: (next: MapFilterState) => void;
  /** Stations the parent currently considers VISIBLE. Drives the
   *  counter chips on the right of the panel. */
  visibleStations: MapStation[];
  /** Phase 6.9 C1 — fit-to-bounds («Все на экран»). The button now
   *  lives inside this expanded panel (spec §A.1); the actual
   *  `fitBounds` call needs the Leaflet map handle, which only
   *  MapScreen holds — so it's wired up through this callback. */
  onFitActive: () => void;
  /** True when at least one station has jobs to fit to. When false
   *  the «Все на экран» button is not rendered. */
  canFit: boolean;
}

export function MapFilters({
  filters,
  onChange,
  visibleStations,
  onFitActive,
  canFit,
}: Props) {
  const [configsOpen, setConfigsOpen] = useState(false);
  // Phase 6.9 C1 — panel collapsed state, seeded from localStorage
  // (default collapsed). The lazy initialiser reads storage once.
  const [collapsed, setCollapsed] = useState<boolean>(_readCollapsed);
  const panelRef = useRef<HTMLDivElement>(null);
  const available = useAvailableConfigs();

  // Operator-facing counters — number of visible markers in two
  // categories. The spec calls out «3 активных | 89 с данными»
  // as the canonical surface.
  const counts = useMemo(() => {
    let active = 0;
    let withData = 0;
    for (const s of visibleStations) {
      if (s.active_now) active += 1;
      if (s.done_with_residual > 0) withData += 1;
    }
    return { active, withData };
  }, [visibleStations]);

  // Phase 6.9 C1 — clicking anywhere outside the expanded panel
  // collapses it (and closes the configs dropdown). Bound only
  // while expanded so the listener isn't a permanent cost.
  useEffect(() => {
    if (collapsed) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setCollapsed(true);
        _writeCollapsed(true);
        setConfigsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [collapsed]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      _writeCollapsed(next);
      if (next) setConfigsOpen(false);
      return next;
    });
  };

  const toggleConfig = (key: string) => {
    onChange({
      ...filters,
      configs: filters.configs.includes(key)
        ? filters.configs.filter((c) => c !== key)
        : [...filters.configs, key],
    });
  };

  return (
    <div
      className={`map-filters ${collapsed ? 'collapsed' : 'expanded'}`}
      data-testid="map-filters"
      ref={panelRef}
    >
      {/* Phase 6.9 C1 — collapsible header strip. Collapsed: this is
          the only thing rendered, showing «Фильтры ▾» + a compact
          counter summary so the operator still sees the headline
          numbers without expanding. */}
      <button
        type="button"
        className="map-filters-header"
        onClick={toggleCollapsed}
        aria-expanded={!collapsed}
        data-testid="map-filters-toggle"
      >
        <span className="map-filters-header-label">Фильтры</span>
        <span className="map-filters-header-caret" aria-hidden="true">
          {collapsed ? '▾' : '▴'}
        </span>
        {collapsed && (
          <span
            className="map-filters-header-summary"
            data-testid="map-filters-summary"
          >
            {counts.active > 0 && (
              <>
                · <strong>{counts.active}</strong> активных
              </>
            )}{' '}
            · <strong>{counts.withData}</strong> с данными
          </span>
        )}
      </button>

      {!collapsed && (
        <>
          {/* Phase 5.2 — two-row layout for the expanded body. Top
              row carries the two controls; bottom row carries the
              counter chips. */}
          <div className="map-filters-row map-filters-row-controls">
            <label className="map-filter-toggle">
              <input
                type="checkbox"
                checked={filters.onlyWithJobs}
                onChange={(e) =>
                  onChange({ ...filters, onlyWithJobs: e.target.checked })
                }
                data-testid="map-filter-only-with-jobs"
              />
              <span>только с задачами</span>
            </label>

            <div className="map-filter-configs-wrap">
              <button
                type="button"
                className="map-filter-configs-button"
                onClick={() => setConfigsOpen((v) => !v)}
                aria-expanded={configsOpen}
                data-testid="map-filter-configs-toggle"
              >
                конфиги:{' '}
                {filters.configs.length === 0
                  ? 'все'
                  : filters.configs
                      .map(
                        (k) =>
                          available.data?.configs.find((c) => c.key === k)
                            ?.label ?? k,
                      )
                      .join(', ')}
                <span
                  className="map-filter-configs-caret"
                  aria-hidden="true"
                >
                  {configsOpen ? '▴' : '▾'}
                </span>
              </button>
              {configsOpen && available.data && (
                <div
                  className="map-filter-configs-dropdown"
                  data-testid="map-filter-configs-dropdown"
                >
                  {available.data.configs.map((c) => {
                    const checked = filters.configs.includes(c.key);
                    return (
                      <label
                        key={c.key}
                        className={`map-filter-config-pill ${
                          checked ? 'active' : ''
                        }`}
                        data-testid={`map-filter-config-${c.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleConfig(c.key)}
                        />
                        <span>{c.label}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div
            className="map-filters-row map-filters-row-counters"
            data-testid="map-filter-counters"
          >
            <span className="map-filter-counter">
              <strong>{counts.active}</strong> активных
            </span>
            <span className="map-filter-counter-sep">·</span>
            <span className="map-filter-counter">
              <strong>{counts.withData}</strong> с данными
            </span>
          </div>

          {/* Phase 6.9 C1 — «Все на экран» fit-to-bounds button.
              Moved here from a standalone top-right map overlay
              (which the expanded panel collided with). Hidden when
              there is no station with jobs to fit to. */}
          {canFit && (
            <button
              type="button"
              className="map-filters-fit-btn"
              onClick={onFitActive}
              data-testid="map-fit-active-btn"
              title="Подобрать масштаб так, чтобы все станции с данными уместились в видимую область"
            >
              Все на экран
            </button>
          )}
        </>
      )}
    </div>
  );
}
